import { Tag } from "@albertine/design-system/components/Tag";
import classNames from "classnames";
import { forwardRef, type SyntheticEvent, useState } from "react";
import { Flex, FlexInline } from "./Flex";
import Label from "./Label";
import "./ChatMessage.css";
import { useAlbertineTranslation } from "../package/albertine-i18n";
import { formatChatMessageDate } from "../utils/time.util";
import Button from "./Button";
import Heading from "./Heading";
import Icon from "./Icon";
import ImageFullSizeButton from "./ImageFullSizeButton";
import Line from "./Line";
import Paragraph from "./Paragraph";

type ChatMessageTopic = {
    title: string;
    onClick: () => void;
};

type ChatMessageAttachment = {
    filename?: string | undefined | null;
    url: string;
    mimeType: string;
    onClick: () => void;
};

type ChatMessageConfirmedBooking = {
    id: string;
    title: string;
    imageUrl: string | undefined | null;
    options?: { id: string; title: string; memberPreferred?: boolean | null }[];
    onClick: () => void;
};

type ChatMessageProposal = {
    id: string | undefined | null;
    title: string | undefined | null;
    bookings: {
        id: string | undefined | null;
        title: string | undefined | null;
        imageURL: string | undefined | null;
        onClick: () => void;
    }[];
    titleOnClick: () => void;
};

type ChatMessageArticle = {
    id: string;
    title: string;
    subtitle: string | undefined | null;
    imageUrl: string | undefined | null;
    onClick: () => void;
};

type ChatMessageQuickRequest = {
    title?: string | undefined | null;
    subtitle?: string | undefined | null;
    questionAnswerPairs?: { question: string; answer: string }[];
};

type ChatMessageArticles = ChatMessageArticle[];

type ChatMessageReply = {
    id: string;
    title: string;
    message: string;
    imageUrl?: string | undefined | null;
    onClick: () => void;
};

// TODO: Import from common Message.ts
type MessageSendingStatus = "sending" | "sent" | "failed";

type ChatMessageNomination = {
    name: string;
    phone: string;
};

export type Props = {
    attachments?: ChatMessageAttachment[];
    avatar?: React.ReactNode;
    className?: string;
    confirmedBookings?: ChatMessageConfirmedBooking[] | undefined;
    id: string;
    label?: string;
    message?: string;
    messageHtml?: string;
    proposal?: ChatMessageProposal;
    reply?: ChatMessageReply;
    senderName?: string;
    sentAt: Date;
    showSenderRow?: boolean;
    topic?: ChatMessageTopic;
    articles?: ChatMessageArticle[];
    quickRequest?: ChatMessageQuickRequest;
    sendingStatus?: MessageSendingStatus;
    nomination?: ChatMessageNomination;
};

const emptyHtml = "<span></span>";

function Bubble(props: { children: React.ReactNode }) {
    const { children } = props;
    return <div className="web-lib__chat-message__bubble">{children}</div>;
}

function MessageText(props: { text?: string; html?: string }) {
    const { text, html } = props;

    if (html && html !== emptyHtml) {
        return (
            <div className="web-lib__chat-message__html">
                <p
                    className="web-lib__chat-message__html"
                    dangerouslySetInnerHTML={{
                        __html: html,
                    }}
                />
            </div>
        );
    }

    if (text) {
        return (
            <div className="web-lib__chat-message__text">
                <p>{text}</p>
            </div>
        );
    }

    return null;
}

function MessageReply(props: ChatMessageReply) {
    const { id, title, message, imageUrl, onClick } = props;
    return (
        <div id={`chat-message-reply-${id}`} className="web-lib__chat-message__reply">
            <Button.Ghost onClick={onClick} className="text-left">
                <Flex.Row gap="value16">
                    {imageUrl && (
                        <div className="web-lib__chat-message__reply__image">
                            <img src={imageUrl} alt={message} />
                        </div>
                    )}
                    <Flex.Column className="web-lib__chat-message__reply__message">
                        <Paragraph.Small.Bold.Text1>{title}</Paragraph.Small.Bold.Text1>
                        <Paragraph.Small.Text2 className="web-lib__chat-message__reply__message-truncate">
                            {message}
                        </Paragraph.Small.Text2>
                    </Flex.Column>
                </Flex.Row>
            </Button.Ghost>
        </div>
    );
}

type FileAttachmentProps = Pick<
    ChatMessageAttachment,
    "filename" | "mimeType" | "onClick"
>;

function FileAttachment(props: FileAttachmentProps) {
    const { filename, mimeType, onClick } = props;
    const formatMimeType = mimeType === "application/pdf" ? "PDF" : mimeType;
    return (
        <Button.Ghost
            onClick={onClick}
            className="web-lib__chat-message__attachment text-left"
        >
            <Flex.Row gap="value8">
                <div className="web-lib__chat-message__attachment-icon">
                    <Icon.Description />
                </div>
                <Flex.Column gap="value4">
                    <Label.Small.MediumBold>{filename}</Label.Small.MediumBold>
                    <Label.Small.Text3>{formatMimeType}</Label.Small.Text3>
                </Flex.Column>
            </Flex.Row>
        </Button.Ghost>
    );
}

type ImageAttachmentProps = Pick<
    ChatMessageAttachment,
    "filename" | "mimeType" | "url"
>;

function ImageAttachment(props: ImageAttachmentProps) {
    const { filename, mimeType, url } = props;
    const t = useAlbertineTranslation();
    if (!mimeType.startsWith("image")) return null;
    return (
        <ImageFullSizeButton
            url={url}
            alt={t("chat_message__image_attachment_alt_text", {
                filename: filename || t("fallback__unknown"),
            })}
            className="web-lib__chat-message__image-attachment"
        />
    );
}

type AudioAttachmentProps = Pick<ChatMessageAttachment, "mimeType" | "url">;

function AudioError(props: { errorMessage: string }) {
    const { errorMessage } = props;
    return (
        <FlexInline gap="value4">
            <Icon.Error.Small.Error.Fill />
            <Label.Small.Error>{errorMessage}</Label.Small.Error>
        </FlexInline>
    );
}

function AudioAttachment(props: AudioAttachmentProps) {
    const { mimeType, url } = props;
    const [displayError, setDisplayError] = useState<string | undefined>();
    const t = useAlbertineTranslation();
    if (!mimeType.startsWith("audio")) return null;
    function handleAudioError(event: SyntheticEvent<HTMLAudioElement>) {
        const audioElement = event.currentTarget;
        const { error } = audioElement;
        if (error) {
            switch (error.code) {
                case MediaError.MEDIA_ERR_ABORTED:
                    break;
                case MediaError.MEDIA_ERR_NETWORK:
                    break;
                case MediaError.MEDIA_ERR_DECODE:
                    console.error(
                        "An error occurred while decoding the audio.",
                        error,
                        url,
                    );
                    setDisplayError(t("chat__audio-error--decode"));
                    break;
                case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
                    console.error(
                        "The audio is missing or is in a format not supported by your browser.",
                        error,
                        url,
                    );
                    setDisplayError(t("chat__audio-error--unsupported"));
                    break;
                default:
                    console.error("Unknown audio playback error.", error, url);
                    break;
            }
        }
    }

    return (
        <>
            <audio controls src={url} onError={handleAudioError}>
                <track kind="captions" srcLang="en" label="Audio message" />
                <AudioError errorMessage={t("chat__audio--not-supported")} />
            </audio>
            {displayError && <AudioError errorMessage={displayError} />}
        </>
    );
}

type ConfirmedBookingProps = Pick<
    ChatMessageConfirmedBooking,
    "title" | "imageUrl" | "onClick" | "options"
>;

function ConfirmedBooking(props: ConfirmedBookingProps) {
    const { title, imageUrl, onClick, options } = props;
    const t = useAlbertineTranslation();
    return (
        <Flex.Column gap="value12">
            <Button.Ghost onClick={onClick} className="text-left">
                <Flex.Row
                    gap="value12"
                    className="web-lib__chat-message__confirmed-booking"
                >
                    {imageUrl && (
                        <div className="web-lib__chat-message__confirmed-booking__image">
                            <img src={imageUrl} alt={title} />
                        </div>
                    )}
                    <div className="web-lib__chat-message__confirmed-booking__title">
                        <Heading.H5>{title}</Heading.H5>
                    </div>
                </Flex.Row>
            </Button.Ghost>
            {options && options.length > 0 && (
                <>
                    <Line.Dashed />
                    <Flex.Column gap="value12">
                        <Label.XSmall.MediumBold.Text2 transform="uppercase">
                            {t("chat__booking-options-preference")}
                        </Label.XSmall.MediumBold.Text2>
                        {options.map((option) => (
                            <Label.Medium.Text1 key={option.id}>
                                {option.title}
                            </Label.Medium.Text1>
                        ))}
                    </Flex.Column>
                </>
            )}
        </Flex.Column>
    );
}

type ProposalProps = Pick<ChatMessageProposal, "title" | "bookings" | "titleOnClick">;

function Proposal(props: ProposalProps) {
    const { title, bookings, titleOnClick } = props;
    const t = useAlbertineTranslation();

    return (
        <Button.Ghost onClick={titleOnClick} className="text-left">
            <Flex.Column className="web-lib__chat-message__proposal" gap="value12">
                <Heading.H5 className="web-lib__chat-message__proposal__title">
                    {title}
                </Heading.H5>

                <Flex.Column gap="value8">
                    {bookings.map((booking) => (
                        <Button.Ghost
                            onClick={booking.onClick}
                            key={booking.id}
                            className="web-lib__chat-message__proposal__booking text-left"
                        >
                            <Flex.Row
                                gap="value8"
                                className="web-lib__chat-message__proposal__images"
                            >
                                <div
                                    key={booking.id}
                                    className="web-lib__chat-message__proposal__image"
                                >
                                    {booking.imageURL && (
                                        <img
                                            src={booking.imageURL}
                                            alt={t(
                                                "chat_message__booking_proposal_alt_text",
                                                {
                                                    title:
                                                        booking.title ||
                                                        t("fallback__unknown_booking"),
                                                },
                                            )}
                                        />
                                    )}
                                </div>
                                <Heading.H5>{booking.title}</Heading.H5>
                            </Flex.Row>
                        </Button.Ghost>
                    ))}
                </Flex.Column>
            </Flex.Column>
        </Button.Ghost>
    );
}

type ArticleSingleProps = Pick<ChatMessageArticle, "title" | "imageUrl" | "onClick">;

function ArticleSingle(props: ArticleSingleProps) {
    const { title, imageUrl, onClick } = props;
    return (
        <Button.Ghost onClick={onClick}>
            <div
                className="web-lib__chat-message__article-single web-lib__chat-message__article-single__background"
                style={{
                    backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
                }}
            >
                <div className="web-lib__chat-message__article-single__background-overlay">
                    <Flex.Column>
                        <Flex.Row gap="value8" justifyContent="space-between">
                            <Heading.H4 className="web-lib__chat-message__article-single__title">
                                {title}
                            </Heading.H4>
                            <div className="web-lib__chat-message__article-arrow">
                                <Icon.ArrowForward.Medium.TextInverse />
                            </div>
                        </Flex.Row>
                    </Flex.Column>
                </div>
            </div>
        </Button.Ghost>
    );
}

type ArticleMultiProps = Pick<
    ChatMessageArticle,
    "title" | "subtitle" | "imageUrl" | "onClick"
>;

function ArticleMulti(props: ArticleMultiProps) {
    const { title, subtitle, imageUrl, onClick } = props;
    return (
        <Button.Ghost onClick={onClick} className="text-left">
            <div className="web-lib__chat-message__article-multi">
                <Flex.Row gap="value16">
                    {imageUrl && (
                        <div className="web-lib__chat-message__article-multi__image">
                            <img src={imageUrl} alt={title} />
                        </div>
                    )}
                    <Flex.Column gap="value8">
                        <Heading.H4>{title}</Heading.H4>
                        {subtitle && (
                            <Label.Medium.Text2>{subtitle}</Label.Medium.Text2>
                        )}
                    </Flex.Column>
                </Flex.Row>
            </div>
        </Button.Ghost>
    );
}

function Articles(props: { articles: ChatMessageArticles }) {
    const { articles } = props;
    if (articles.length === 0) return null;
    if (articles.length === 1) return <ArticleSingle {...articles[0]} />;
    return (
        <Flex.Column gap="value8" className="web-lib__chat-message__articles">
            {articles.map((article) => (
                <ArticleMulti key={article.id} {...article} />
            ))}
        </Flex.Column>
    );
}

function QuickRequest(props: { quickRequest: ChatMessageQuickRequest }) {
    const { quickRequest } = props;
    const { title, subtitle, questionAnswerPairs } = quickRequest;
    const noData =
        !title &&
        !subtitle &&
        (!questionAnswerPairs || questionAnswerPairs.length === 0);
    if (noData) return null;
    return (
        <Flex.Column gap="value12" className="web-lib__chat-message__quick-request">
            <Flex.Column gap="value8">
                <Heading.H5>{title}</Heading.H5>
                {subtitle && <Heading.H6>{subtitle}</Heading.H6>}
            </Flex.Column>
            {questionAnswerPairs && questionAnswerPairs.length > 0 && (
                <>
                    <Line.Dashed />
                    {questionAnswerPairs.map((pair) => (
                        <Flex.Column key={pair.question} gap="value4">
                            <Label.Small.MediumBold>
                                {pair.question}
                            </Label.Small.MediumBold>
                            <Label.Small.Text2>{pair.answer}</Label.Small.Text2>
                        </Flex.Column>
                    ))}
                </>
            )}
        </Flex.Column>
    );
}

function MessageLabel(props: { label?: string }) {
    const { label } = props;
    if (!label) return null;
    return <Tag className="web-lib__chat-message__label">{label}</Tag>;
}

function Time(props: { sentAt?: Date }) {
    const { sentAt } = props;
    if (!sentAt) return null;
    return (
        <Label.XSmall.MediumBold.Text1 nowrap className="web-lib__chat-message__time">
            {formatChatMessageDate(sentAt)}
        </Label.XSmall.MediumBold.Text1>
    );
}

function Sender(props: { senderName?: string }) {
    const { senderName } = props;
    if (!senderName) return null;
    return (
        <Label.XSmall.MediumBold.Text3 nowrap className="web-lib__chat-message__sender">
            {senderName}
        </Label.XSmall.MediumBold.Text3>
    );
}

function Topic(props: { topic?: ChatMessageTopic }) {
    const { topic } = props;
    if (!topic) return null;
    return (
        <Button.Ghost onClick={topic.onClick} className="web-lib__chat-message__topic">
            <Label.XSmall.MediumBold.Text1 nowrap>
                {topic.title}
            </Label.XSmall.MediumBold.Text1>
        </Button.Ghost>
    );
}

function SendingStatus(props: { sendingStatus: MessageSendingStatus }) {
    const { sendingStatus } = props;
    const t = useAlbertineTranslation();
    if (sendingStatus === "sending") {
        return (
            <Label.Small.Text2 className="web-lib__chat-message__sending-status">
                {t("chat__sending-message")}
            </Label.Small.Text2>
        );
    }
    if (sendingStatus === "failed") {
        return (
            <FlexInline className="web-lib__chat-message__sending-status" gap="value4">
                <Icon.Error.Small.Error.Fill />
                <Label.Small.Error>
                    {t("chat__sending-message--failed")}
                </Label.Small.Error>
            </FlexInline>
        );
    }
    return null;
}

type TaggedArticleProps = Pick<ChatMessageArticle, "title" | "imageUrl" | "onClick">;

function TaggedArticle(props: TaggedArticleProps) {
    const { title, imageUrl, onClick } = props;
    return (
        <Button.Ghost onClick={onClick} className="text-left">
            <Flex.Column className="web-lib__chat-message__proposal" gap="value12">
                <Flex.Row gap="value16">
                    {imageUrl && (
                        <div className="web-lib__chat-message__proposal__image">
                            <img src={imageUrl} alt={title} />
                        </div>
                    )}

                    <Flex.Column gap="value4">
                        <Heading.H5>{title}</Heading.H5>
                    </Flex.Column>
                </Flex.Row>
            </Flex.Column>
        </Button.Ghost>
    );
}

function NominationHeader(props: { nomination: ChatMessageNomination }) {
    const { nomination } = props;
    const t = useAlbertineTranslation();
    if (!nomination) return null;
    return (
        <>
            <Heading.H5>{t("chat__nomination-title")}</Heading.H5>
            <Line.Dashed />
        </>
    );
}

function NominationMessage(props: { nomination: ChatMessageNomination }) {
    const { nomination } = props;
    const t = useAlbertineTranslation();
    if (!nomination) return null;
    return (
        <Flex.Column gap="value16">
            <Flex.Column gap="value8">
                <Label.Small.MediumBold>
                    {t("chat__nomination-name")}
                </Label.Small.MediumBold>
                <Label.Small.Text1>{nomination.name}</Label.Small.Text1>
            </Flex.Column>
            <Flex.Column gap="value8">
                <Label.Small.MediumBold>
                    {t("chat__nomination-phone-number")}
                </Label.Small.MediumBold>
                <Label.Small.Text1>{nomination.phone}</Label.Small.Text1>
            </Flex.Column>
        </Flex.Column>
    );
}

function MessageBubble(props: Props & { isMemberMessage: boolean }) {
    const {
        proposal,
        quickRequest,
        isMemberMessage,
        label,
        confirmedBookings,
        reply,
        message,
        messageHtml,
        articles,
        nomination,
    } = props;

    const hasMessage = message ?? (messageHtml && messageHtml !== emptyHtml);
    const hasConfirmedBookings = confirmedBookings && confirmedBookings.length > 0;

    return (
        <Bubble>
            <MessageLabel label={label} />
            <Flex.Column gap="value16">
                {quickRequest && <QuickRequest quickRequest={quickRequest} />}

                <ProposalOrTaggedArticle
                    proposal={proposal}
                    articles={articles}
                    hasMessage={hasMessage}
                    isMemberMessage={isMemberMessage}
                />

                {hasConfirmedBookings && (
                    <>
                        <Flex.Column gap="value8">
                            {confirmedBookings.map((booking) => (
                                <ConfirmedBooking key={booking.id} {...booking} />
                            ))}
                        </Flex.Column>
                        {hasMessage && <Line.Dashed />}
                    </>
                )}
                {reply && <MessageReply {...reply} />}
                {nomination && NominationHeader({ nomination })}
                {!!hasMessage && <MessageText text={message} html={messageHtml} />}
                {nomination && NominationMessage({ nomination })}
            </Flex.Column>
        </Bubble>
    );
}

function ProposalOrTaggedArticle({
    proposal,
    articles,
    hasMessage,
    isMemberMessage,
}: {
    proposal: ChatMessageProposal | undefined;
    articles: ChatMessageArticle[] | undefined;
    hasMessage: string | boolean | undefined;
    isMemberMessage: boolean;
}) {
    if (proposal) {
        return (
            <>
                <Proposal {...proposal} />
                {hasMessage && <Line.Dashed />}
            </>
        );
    }

    if (isMemberMessage && articles && articles.length > 0) {
        return (
            <>
                <TaggedArticle {...articles[0]} />
                {hasMessage && <Line.Dashed />}
            </>
        );
    }

    return null;
}

function BaseChatMessage(baseClassName: "agent" | "member") {
    return function ChatMessage(props: Props) {
        const {
            attachments,
            avatar,
            className,
            id,
            senderName,
            sentAt,
            showSenderRow = true,
            topic,
            articles,
            sendingStatus,
            message,
            messageHtml,
        } = props;
        const baseClass = `web-lib__chat-message--${baseClassName}`;

        const fileAttachments = attachments?.filter(
            (attachment) =>
                !attachment.mimeType.startsWith("image") &&
                !attachment.mimeType.startsWith("audio"),
        );
        const imageAttachments = attachments?.filter((attachment) =>
            attachment.mimeType.startsWith("image"),
        );
        const audioAttachments = attachments?.filter((attachment) =>
            attachment.mimeType.startsWith("audio"),
        );

        const isMemberMessage = baseClassName === "member";
        const hasArticles = articles && articles.length > 0;
        const hasMessage = message ?? (messageHtml && messageHtml !== emptyHtml);

        return (
            <div
                id={`chat-message__${id}`}
                className={classNames(
                    "web-lib__chat-message",
                    `web-lib__chat-message--${sendingStatus}`,
                    baseClass,
                    className,
                )}
            >
                <Flex.Column gap="value8">
                    {fileAttachments && fileAttachments.length > 0 && (
                        <Flex.Column
                            gap="value8"
                            alignItems={baseClassName === "member" ? "end" : "start"}
                            className="web-lib__chat-message__attachments"
                        >
                            {fileAttachments.map((attachment) => (
                                <Bubble key={attachment.url}>
                                    <FileAttachment
                                        key={attachment.url}
                                        {...attachment}
                                    />
                                </Bubble>
                            ))}
                        </Flex.Column>
                    )}
                    {imageAttachments && imageAttachments.length > 0 && (
                        <Flex.Column
                            gap="value8"
                            alignItems={baseClassName === "member" ? "end" : "start"}
                            className="web-lib__chat-message__image-attachments"
                        >
                            {imageAttachments.map((attachment) => (
                                <Bubble key={attachment.url}>
                                    <ImageAttachment {...attachment} />
                                </Bubble>
                            ))}
                        </Flex.Column>
                    )}
                    {audioAttachments && audioAttachments.length > 0 && (
                        <Flex.Column
                            gap="value8"
                            alignItems={baseClassName === "member" ? "end" : "start"}
                            className="web-lib__chat-message__audio-attachments"
                        >
                            {audioAttachments.map((attachment) => (
                                <Bubble key={attachment.url}>
                                    <AudioAttachment {...attachment} />
                                </Bubble>
                            ))}
                        </Flex.Column>
                    )}

                    {!isMemberMessage && hasArticles && (
                        <Articles articles={articles} />
                    )}

                    {(!isMemberMessage || (isMemberMessage && !!hasMessage)) && (
                        <Flex.Row
                            gap="value8"
                            className="web-lib__chat-message--row-reversed"
                            alignItems="end"
                        >
                            {!isMemberMessage &&
                                (showSenderRow ? (
                                    avatar
                                ) : (
                                    <div className="chat__message__avatar-spacer" />
                                ))}

                            <MessageBubble
                                {...props}
                                isMemberMessage={isMemberMessage}
                            />
                        </Flex.Row>
                    )}

                    <Flex.Row
                        gap="value8"
                        alignItems="end"
                        className={classNames(
                            "web-lib__chat-message__bottom-row",
                            "web-lib__chat-message--row-reversed",
                            {
                                "web-lib__chat-message__bottom-row--hidden":
                                    showSenderRow !== undefined && !showSenderRow,
                            },
                        )}
                    >
                        {sendingStatus === "sending" || sendingStatus === "failed" ? (
                            <SendingStatus sendingStatus={sendingStatus} />
                        ) : (
                            <>
                                <Time sentAt={sentAt} />
                                <Sender senderName={senderName} />
                                <Topic topic={topic} />
                            </>
                        )}
                    </Flex.Row>
                </Flex.Column>
            </div>
        );
    };
}

const Wrapper = forwardRef<HTMLDivElement, { children: React.ReactNode }>(
    (props, ref) => {
        const { children } = props;

        return (
            <div ref={ref} className="web-lib__chat-message__background">
                {children}
            </div>
        );
    },
);

const ChatMessage = {
    Wrapper,
    Agent: BaseChatMessage("agent"),
    Member: BaseChatMessage("member"),
};

export default ChatMessage;
